import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { User, Briefcase } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { profileApi } from '../api/client';

const inputClass = 'w-full px-3.5 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-shadow';

function Field({ label, error, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1.5">
        {label}{hint && <span className="ml-1.5 text-xs text-muted-foreground font-normal">{hint}</span>}
      </label>
      {children}
      {error && <p className="text-xs text-destructive mt-1">Dit veld is verplicht</p>}
    </div>
  );
}

export default function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('choose');
  const [saving, setSaving] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: { country: 'BE' },
  });

  const onSubmit = async (data) => {
    if (!user) return;
    setSaving(true);
    try {
      await profileApi.createCustomer(data);
      navigate('/');
    } catch (err) {
      alert(err.response?.data?.detail || 'Er is een fout opgetreden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[75vh] flex items-center justify-center px-6 py-12" data-testid="onboarding-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-10">
          <h1 className="font-serif text-4xl font-light mb-2 text-foreground">Welkom bij The Herborist</h1>
          <p className="text-muted-foreground">Vertel ons wie u bent zodat we u beter kunnen bedienen.</p>
        </div>

        {step === 'choose' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <button
              data-testid="choose-customer"
              onClick={() => setStep('customer-form')}
              className="group bg-card border-2 border-border hover:border-primary rounded-2xl p-6 text-left transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
            >
              <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <User className="w-5 h-5" />
              </div>
              <h2 className="font-serif text-lg font-medium mb-1 text-foreground">Particuliere klant</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">Ik koop producten voor persoonlijk gebruik.</p>
            </button>

            <button
              data-testid="choose-b2b"
              onClick={() => navigate('/register/b2b')}
              className="group bg-card border-2 border-border hover:border-primary rounded-2xl p-6 text-left transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
            >
              <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <Briefcase className="w-5 h-5" />
              </div>
              <h2 className="font-serif text-lg font-medium mb-1 text-foreground">Professional</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">Ik ben zorgprofessional en wil een B2B-account.</p>
            </button>
          </div>
        )}

        {step === 'customer-form' && (
          <form onSubmit={handleSubmit(onSubmit)} className="bg-card border border-border rounded-2xl p-6 space-y-4 shadow-sm" data-testid="customer-form">
            <h2 className="font-serif text-xl font-light mb-2 text-foreground">Uw gegevens</h2>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Voornaam" error={errors.firstName}>
                <input data-testid="first-name" {...register('firstName', { required: true })} className={inputClass} />
              </Field>
              <Field label="Achternaam" error={errors.lastName}>
                <input data-testid="last-name" {...register('lastName', { required: true })} className={inputClass} />
              </Field>
            </div>

            <Field label="E-mail">
              <input type="email" value={user?.email || ''} readOnly className={`${inputClass} bg-muted text-muted-foreground cursor-not-allowed`} />
            </Field>

            <Field label="Telefoon" hint="(optioneel)">
              <input type="tel" {...register('phone')} className={inputClass} />
            </Field>

            <div className="border-t border-border pt-4">
              <p className="text-sm font-medium text-foreground mb-3">Leveringsadres</p>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <Field label="Straat" error={errors.street}>
                      <input {...register('street', { required: true })} className={inputClass} />
                    </Field>
                  </div>
                  <Field label="Huisnummer" error={errors.number}>
                    <input {...register('number', { required: true })} className={inputClass} />
                  </Field>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Bus" hint="(opt.)">
                    <input {...register('bus')} className={inputClass} />
                  </Field>
                  <Field label="Postcode" error={errors.postalCode}>
                    <input {...register('postalCode', { required: true })} className={inputClass} />
                  </Field>
                  <Field label="Stad" error={errors.city}>
                    <input {...register('city', { required: true })} className={inputClass} />
                  </Field>
                </div>
                <div className="max-w-[160px]">
                  <Field label="Land">
                    <input {...register('country')} className={inputClass} />
                  </Field>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                data-testid="submit-customer"
                type="submit" disabled={saving}
                className="flex-1 bg-primary text-primary-foreground rounded-full py-3 font-semibold hover:bg-primary/90 disabled:opacity-50 transition-all active:scale-95"
              >
                {saving ? 'Opslaan...' : 'Doorgaan naar de winkel'}
              </button>
              <button type="button" onClick={() => setStep('choose')}
                className="px-6 py-3 border border-border rounded-full text-sm font-medium hover:bg-muted transition-colors">
                Terug
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
